import { onActivateKey } from './activate';
import { cx } from './classNames';

/** Seitenblätterung der `SimpleTable` mit den `e-pager`-Klassen. */
export interface SimpleTablePagerProps {
  /** 1-basiert. */
  page: number;
  pageCount: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  /** Höchstzahl sichtbarer Seitenschaltflächen; Syncfusion-Standard 8. */
  maxPageButtons?: number;
}

interface NavProps {
  className: string;
  icon: string;
  label: string;
  disabled: boolean;
  onClick: () => void;
}

function Nav(props: NavProps) {
  const activate = () => {
    if (!props.disabled) props.onClick();
  };

  return (
    <div
      role="button"
      // Deaktivierte Schaltflächen bleiben sichtbar, fallen aber aus der
      // Tabulatorreihenfolge — wie bei einem `<button disabled>`.
      tabIndex={props.disabled ? -1 : 0}
      aria-label={props.label}
      aria-disabled={props.disabled}
      className={cx(props.disabled ? `${props.className}disabled` : props.className, 'e-icons')}
      onClick={activate}
      onKeyDown={onActivateKey(activate)}
    >
      <i className={props.icon} />
    </div>
  );
}

function pageWindow(page: number, pageCount: number, size: number): number[] {
  const start = Math.max(1, Math.min(page - Math.floor(size / 2), pageCount - size + 1));
  const length = Math.min(size, pageCount);
  return Array.from({ length }, (_unused, index) => start + index);
}

export function SimpleTablePager(props: SimpleTablePagerProps) {
  const { onPageChange, page, pageCount } = props;
  const pages = pageWindow(page, pageCount, props.maxPageButtons ?? 8);

  return (
    <div className="e-pager">
      <div className="e-pagercontainer">
        <Nav
          className="e-firstpage"
          icon="fa-solid fa-angles-left"
          label="Erste Seite"
          disabled={page <= 1}
          onClick={() => {
            onPageChange(1);
          }}
        />
        <Nav
          className="e-prevpage"
          icon="fa-solid fa-arrow-left"
          label="Vorherige Seite"
          disabled={page <= 1}
          onClick={() => {
            onPageChange(page - 1);
          }}
        />
        {/* `a` ohne `href` ist nicht fokussierbar; die Klassennamen sind aber
            verbindlich (REQ-00 §13), das Element also nicht austauschbar. */}
        {pages.map((number) => (
          <a
            key={number}
            role="button"
            tabIndex={0}
            className={cx('e-numericitem', number === page && 'e-currentitem')}
            aria-current={number === page}
            onClick={() => {
              onPageChange(number);
            }}
            onKeyDown={onActivateKey(() => {
              onPageChange(number);
            })}
          >
            {number}
          </a>
        ))}
        <Nav
          className="e-nextpage"
          icon="fa-solid fa-arrow-right"
          label="Nächste Seite"
          disabled={page >= pageCount}
          onClick={() => {
            onPageChange(page + 1);
          }}
        />
        <Nav
          className="e-lastpage"
          icon="fa-solid fa-angles-right"
          label="Letzte Seite"
          disabled={page >= pageCount}
          onClick={() => {
            onPageChange(pageCount);
          }}
        />
      </div>
      <div className="e-parentmsgbar">
        <span className="e-pagermessage">
          {page} of {pageCount} pages ({props.totalCount} items)
        </span>
      </div>
    </div>
  );
}

export default SimpleTablePager;
